/*
 * halal-label/label-layout.js — an Indonesian food label as millimetres.
 *
 * Output is the ITEM LIST of the label-maker engine (public/js/label-maker):
 * text / shape / image / barcode items in label millimetres, consumed as-is by
 * LabelRender (preview) and LabelPdf (PDF). Two things that engine does not
 * draw are returned separately as `ext` items and drawn by halal-pdf.js and
 * the page with the same numbers:
 *   - italic text — the ING footnote MUST be italic (PerBPOM 10/2026 Lamp. I
 *     angka 6 huruf a); drawn as an oblique of the same face in both backends;
 *   - vector paths — the official NON HALAL / MENGANDUNG BABI marks traced
 *     from BPJPH's files (resources/data/halal-label/non-halal-marks.json).
 *
 * TYPE SIZE IS SOLVED, NOT PICKED. One body size `b` drives the whole label;
 * it is the largest size at which every panel fits, searched between the
 * legal floor (Pasal 9: lower-case "o" >= 1 mm, or 0,75 mm on a label of
 * <= 10 cm2) and a comfortable ceiling. Product name and warnings never go
 * under "o" = 2 mm whatever `b` is. If nothing fits at the floor, the label
 * says how many millimetres it is short instead of printing illegal type.
 *
 * `measure(text, {font, bold, size})` → width in mm, with `.ascentEm(style)`
 * and `.oRatio(style)` (height of "o" / type size, measured on the face).
 * The page passes LabelRender.measurer(); tests pass a fixed-width stand-in.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalLayout = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var LINE = 1.18;          // line height / type size
    var PARA_GAP = 0.45;      // gap after a paragraph, in body line heights
    var B_MAX = 4.2;          // mm — about 12 pt; a label never needs more
    var OBLIQUE_DEG = 12;     // italic as an oblique of the upright face

    function rolesFor(b, sizeForO, oMin) {
        // prominent text: never below "o" = 2 mm (Pasal 9(3)) unless the label
        // is tiny (<= 10 cm2), where the act lowers everything to 0,75 mm
        var prom = Math.max(b * 1.15, sizeForO(oMin.prominent));
        return {
            body: b,
            small: b,
            prominent: prom,
            title: Math.max(b * 1.55, prom),
            brand: Math.max(b * 2.1, prom * 1.25),
        };
    }

    /* ── rich text wrapping ──────────────────────────────────────────────
     * runs: [{t, b, i}] → lines: [{segs:[{t, b, i, x, w}], w}]. Spaces at a
     * break are dropped; a word longer than the line is split by characters
     * (an address with no spaces must not run off the label). */
    function wrapRuns(runs, maxW, size, font, measure) {
        var tokens = [];
        (runs || []).forEach(function (r) {
            var parts = String(r.t || '').split(/(\s+)/);
            parts.forEach(function (p) {
                if (p === '') return;
                // hard line break inside a run
                if (/\n/.test(p)) { tokens.push({ br: true }); return; }
                tokens.push({ t: p, b: !!r.b, i: !!r.i, sp: /^\s+$/.test(p) });
            });
        });
        var lines = [];
        var cur = { segs: [], w: 0 };
        var pendingSpace = null;
        function width(t, tok) { return measure(t, { font: font, bold: tok.b, size: size }); }
        function push(tok, text) {
            var w = width(text, tok);
            var last = cur.segs[cur.segs.length - 1];
            if (last && last.b === tok.b && last.i === tok.i) {
                last.t += text;
                last.w = width(last.t, last);
            } else {
                cur.segs.push({ t: text, b: tok.b, i: tok.i, x: cur.w, w: w });
            }
            recompute(cur);
        }
        function newLine() {
            lines.push(cur);
            cur = { segs: [], w: 0 };
            pendingSpace = null;
        }
        for (var k = 0; k < tokens.length; k++) {
            var tok = tokens[k];
            if (tok.br) { newLine(); continue; }
            if (tok.sp) { if (cur.segs.length) pendingSpace = tok; continue; }
            var ww = width(tok.t, tok);
            var spaceW = pendingSpace ? width(' ', pendingSpace) : 0;
            if (cur.segs.length && cur.w + spaceW + ww > maxW + 1e-6) {
                newLine();
                spaceW = 0;
            }
            if (ww > maxW + 1e-6) {
                // split an over-long word by characters
                var chunk = '';
                for (var c = 0; c < tok.t.length; c++) {
                    var next = chunk + tok.t[c];
                    if (width(next, tok) > maxW && chunk) {
                        if (cur.segs.length) newLine();
                        push(tok, chunk);
                        newLine();
                        chunk = tok.t[c];
                    } else chunk = next;
                }
                if (chunk) {
                    if (pendingSpace && cur.segs.length) push(pendingSpace, ' ');
                    push(tok, chunk);
                }
                pendingSpace = null;
                continue;
            }
            if (pendingSpace && cur.segs.length) push({ b: pendingSpace.b, i: pendingSpace.i }, ' ');
            pendingSpace = null;
            push(tok, tok.t);
        }
        if (cur.segs.length || !lines.length) lines.push(cur);
        // positions from the measured widths
        lines.forEach(recompute);
        return lines;

        function recompute(line) {
            var x = 0;
            line.segs.forEach(function (s) {
                s.x = x; s.w = width(s.t, s); x += s.w;
                var lead = s.t.match(/^\s+/);
                s.lw = lead ? width(lead[0], s) : 0;
            });
            line.w = x;
        }
    }

    /* Emit a wrapped paragraph as items at (x, y) within width w. */
    function emitPara(out, lines, x, y, w, size, font, color, align, ascentEm) {
        var lh = size * LINE;
        lines.forEach(function (line, li) {
            var off = 0;
            if (align === 'center') off = Math.max(0, (w - line.w) / 2);
            else if (align === 'right') off = Math.max(0, w - line.w);
            line.segs.forEach(function (s) {
                if (!s.t.trim()) return;
                /* A run that begins with a space ("Tempe kedelai" + " (60%)"):
                   SVG collapses leading white space, PDF draws it — so the
                   space becomes an x offset and neither backend sees it. */
                var lead = s.t.match(/^\s+/);
                var t = s.t, sx = s.x, sw = s.w;
                if (lead) {
                    var lw = s.lw != null ? s.lw : 0;
                    t = s.t.slice(lead[0].length);
                    sx += lw; sw -= lw;
                }
                var it = {
                    kind: 'text', lines: [t], font: font, bold: s.b, size: size,
                    lineHeight: lh, align: 'left', valign: 'top', color: color,
                    ascentEm: ascentEm(s.b), letterSpacing: 0,
                    box: { x: x + off + sx, y: y + li * lh, w: sw + size * 0.2, h: lh },
                };
                if (s.i) { it.italic = true; out.ext.push(it); } else out.items.push(it);
            });
        });
        return lines.length * lh;
    }

    /* ── the ING table ────────────────────────────────────────────────────
     * Formats from Kepka 265/2026 Lamp. II B.6 and PerBPOM 10/2026 Lamp. I.II:
     *   'vertical'  — boxed, two-column numbers, footnote in italics;
     *   'tabular'   — horizontal: title block left, numbers right;
     *   'linear'    — one paragraph in a frame;
     * and the per-100 g frame for pangan olahan antara / "Per 100 g" kinds
     * (no %AKG, no footnote: Lamp. I.6.b). `res` is HalalIng.compute(). */
    var LABELS = {
        energy: 'Energi total', fat: 'Lemak total', sat: 'Lemak jenuh', protein: 'Protein',
        carb: 'Karbohidrat total', sugar: 'Gula total', sodium: 'Garam (Natrium)',
    };
    var FOOT = '*Persen AKG berdasarkan kebutuhan energi 2150 kkal. Kebutuhan energi anda mungkin lebih tinggi atau lebih rendah.';

    function fmtNum(v, dec) {
        return Number(v).toFixed(dec).replace('.', ',');
    }

    function amountText(r) { return fmtNum(r.value, r.decimals) + ' ' + r.unit; }

    function servingLine(res, urt) {
        var u = res.unit || 'g';
        var s = 'Takaran saji ' + fmtNum(res.serving, res.serving % 1 ? 1 : 0) + ' ' + u;
        if (urt) s += ' (' + urt + ')';
        return s;
    }

    function sppLine(res) {
        if (res.single || res.spp == null) return null;
        return fmtNum(res.spp, res.spp % 1 ? 1 : 0) + ' Sajian per Kemasan';
    }

    function headLine(res) {
        if (res.basis === 'pack') return 'JUMLAH PER KEMASAN (' + fmtNum(res.massG, res.massG % 1 ? 1 : 0) + ' ' + (res.unit || 'g') + ')';
        if (res.basis === '100') return 'JUMLAH PER 100 ' + (res.unit || 'g');
        return 'JUMLAH PER SAJIAN';
    }

    /* Plain-text ING, also the payload of the QR (2D barcode) variant. */
    function ingText(res, urt) {
        if (!res || !res.rows || !res.rows.length) return '';
        var parts = ['INFORMASI NILAI GIZI'];
        if (res.basis === 'serving') {
            var s = servingLine(res, urt);
            var spp = sppLine(res);
            parts.push(s + (spp ? ', ' + spp : ''));
        }
        var body = res.rows.map(function (r) {
            var t = LABELS[r.key] + ' ' + amountText(r);
            if (r.akg != null && res.basis !== '100') t += ' (' + r.akg + '% AKG)';
            return t;
        }).join(', ');
        parts.push(headLine(res) + ': ' + body);
        if (res.basis !== '100') parts.push(FOOT);
        return parts.join('. ').replace(/\.\./g, '.');
    }

    function ingBlock(res, opts, x, y, w, b, font, measure, out) {
        var fmtName = opts.format || 'vertical';
        if (res.basis === '100') fmtName = 'per100';
        if (fmtName === 'linear') return ingLinear(res, opts, x, y, w, b, font, measure, out);
        if (fmtName === 'tabular') return ingTabular(res, opts, x, y, w, b, font, measure, out);
        return ingVertical(res, opts, x, y, w, b, font, measure, out, fmtName === 'per100');
    }

    function strokeRect(out, x, y, w, h, sw, color) {
        out.items.push({ kind: 'shape', shape: 'rect', box: { x: x, y: y, w: w, h: h },
            stroke: color, strokeWidth: sw, fill: null });
    }
    function hline(out, x, y, w, sw, color) {
        // a filled thin rectangle: the engine's 'line' shape caps its ends
        out.items.push({ kind: 'shape', shape: 'rect', box: { x: x, y: y - sw / 2, w: w, h: sw }, fill: color });
    }

    function ingVertical(res, opts, x0, y0, wAvail, b, font, measure, out, per100) {
        var color = opts.color || '#000000';
        var size = b;
        var lh = size * LINE;
        var asc = function (bold) { return measure.ascentEm({ font: font, bold: bold, size: size }); };
        var pad = Math.max(0.8, size * 0.45);
        // natural width: widest label + value + %; at least the title
        var mw = function (t, bold, s) { return measure(t, { font: font, bold: bold, size: s || size }); };
        var colLab = 0, colVal = 0, colPct = mw('100 %', true);
        res.rows.forEach(function (r) {
            colLab = Math.max(colLab, mw(LABELS[r.key], true));
            colVal = Math.max(colVal, mw(amountText(r), true));
        });
        var titleSize = size * 1.15;
        var natural = Math.max(colLab + colVal + (per100 ? 0 : colPct) + size * 2.4, mw('INFORMASI NILAI GIZI', true, titleSize)) + 2 * pad;
        var w = Math.min(wAvail, Math.max(natural, Math.min(wAvail, 40)));
        var x = x0 + (wAvail - w) / 2;
        var ix = x + pad, iw = w - 2 * pad;
        var y = y0 + pad * 0.6;
        var thin = Math.max(0.15, size * 0.06), thick = Math.max(0.35, size * 0.16);

        // title
        y += emitPara(out, [{ segs: [{ t: 'INFORMASI NILAI GIZI', b: true, x: 0, w: mw('INFORMASI NILAI GIZI', true, titleSize) }], w: mw('INFORMASI NILAI GIZI', true, titleSize) }],
            ix, y, iw, titleSize, font, color, 'center', function (bd) { return measure.ascentEm({ font: font, bold: bd, size: titleSize }); });
        if (!per100 && res.basis === 'serving') {
            y += lh * 0.25;
            var sl = wrapRuns([{ t: servingLine(res, opts.urt), b: false }], iw, size, font, measure);
            y += emitPara(out, sl, ix, y, iw, size, font, color, 'left', asc);
            var spp = sppLine(res);
            if (spp) y += emitPara(out, wrapRuns([{ t: spp, b: false }], iw, size, font, measure), ix, y, iw, size, font, color, 'left', asc);
        }
        y += lh * 0.2;
        hline(out, x, y, w, thick, color);
        y += thick / 2 + lh * 0.15;
        // JUMLAH PER …
        y += emitPara(out, wrapRuns([{ t: headLine(res), b: true }], iw, size, font, measure), ix, y, iw, size, font, color, 'left', asc);

        var rows = res.rows.slice();
        function row(label, value, pct, bold) {
            var lw = mw(label, bold);
            out.items.push(textItem(label, ix, y, lw, size, font, bold, color, asc(bold), 'left'));
            var vw = mw(value, bold);
            var vRight = per100 ? ix + iw : ix + iw - colPct - size * 1.2;
            out.items.push(textItem(value, vRight - vw, y, vw, size, font, bold, color, asc(bold), 'left'));
            if (pct != null) {
                var pw = mw(pct, bold);
                out.items.push(textItem(pct, ix + iw - pw, y, pw, size, font, bold, color, asc(bold), 'left'));
            }
            y += lh;
        }
        // energy first, on its own
        var energy = rows.shift();
        row(LABELS.energy, amountText(energy), null, true);
        if (!per100) {
            y += lh * 0.1;
            hline(out, x, y, w, thin, color);
            y += thin / 2 + lh * 0.1;
            var akgH = '% AKG*';
            var aw = mw(akgH, true);
            out.items.push(textItem(akgH, ix + iw - aw, y, aw, size, font, true, color, asc(true), 'left'));
            y += lh;
        }
        rows.forEach(function (r) {
            var pct = (!per100 && r.akg != null) ? r.akg + ' %' : null;
            row(LABELS[r.key], amountText(r), pct, true);
        });
        if (!per100) {
            y += lh * 0.15;
            hline(out, x, y, w, thin, color);
            y += thin / 2 + lh * 0.15;
            var foot = wrapRuns([{ t: FOOT, b: false, i: true }], iw, size, font, measure);
            y += emitPara(out, foot, ix, y, iw, size, font, color, 'left', asc);
        }
        y += pad * 0.6;
        strokeRect(out, x, y0, w, y - y0, thin * 1.4, color);
        return { h: y - y0, w: w, x: x };
    }

    function textItem(t, x, y, w, size, font, bold, color, ascentEm, align) {
        return {
            kind: 'text', lines: [t], font: font, bold: !!bold, size: size, lineHeight: size * LINE,
            align: align || 'left', valign: 'top', color: color, ascentEm: ascentEm, letterSpacing: 0,
            box: { x: x, y: y, w: w + size * 0.2, h: size * LINE },
        };
    }

    function ingTabular(res, opts, x0, y0, w, b, font, measure, out) {
        var color = opts.color || '#000000';
        var size = b, lh = size * LINE;
        var asc = function (bold) { return measure.ascentEm({ font: font, bold: bold, size: size }); };
        var mw = function (t, bold, s) { return measure(t, { font: font, bold: bold, size: s || size }); };
        var pad = Math.max(0.8, size * 0.45);
        var thin = Math.max(0.15, size * 0.06);
        var leftW = Math.max(w * 0.38, mw('INFORMASI', true, size * 1.5) + 2 * pad);
        var rightW = w - leftW;
        var lx = x0 + pad, liw = leftW - 2 * pad;
        var rx = x0 + leftW + pad, riw = rightW - 2 * pad;
        var y = y0 + pad * 0.6;
        // left: title in two lines, serving, servings, energy
        var tsz = size * 1.5;
        var tl = wrapRuns([{ t: 'INFORMASI NILAI GIZI', b: true }], liw, tsz, font, measure);
        var yl = y + emitPara(out, tl, lx, y, liw, tsz, font, color, 'left', function (bd) { return measure.ascentEm({ font: font, bold: bd, size: tsz }); });
        yl += lh * 0.3;
        if (res.basis === 'serving') {
            yl += emitPara(out, wrapRuns([{ t: servingLine(res, opts.urt), b: false }], liw, size, font, measure), lx, yl, liw, size, font, color, 'left', asc);
            var spp = sppLine(res);
            if (spp) yl += emitPara(out, wrapRuns([{ t: spp, b: false }], liw, size, font, measure), lx, yl, liw, size, font, color, 'left', asc);
        } else {
            yl += emitPara(out, wrapRuns([{ t: headLine(res), b: true }], liw, size, font, measure), lx, yl, liw, size, font, color, 'left', asc);
        }
        var e = res.rows[0];
        var ev = amountText(e);
        out.items.push(textItem(LABELS.energy, lx, yl, mw(LABELS.energy, true), size, font, true, color, asc(true)));
        out.items.push(textItem(ev, lx + liw - mw(ev, true), yl, mw(ev, true), size, font, true, color, asc(true)));
        yl += lh;
        // right: header row and the six rows
        var yr = y;
        var hd = res.basis === 'serving' ? 'JUMLAH PER SAJIAN' : 'JUMLAH';
        out.items.push(textItem(hd, rx, yr, mw(hd, true), size, font, true, color, asc(true)));
        var ah = '%AKG*';
        out.items.push(textItem(ah, rx + riw - mw(ah, true), yr, mw(ah, true), size, font, true, color, asc(true)));
        yr += lh * 1.05;
        hline(out, x0 + leftW, yr - lh * 0.08, rightW, thin, color);
        var pctW = mw('100%', true);
        res.rows.slice(1).forEach(function (r) {
            out.items.push(textItem(LABELS[r.key], rx, yr, mw(LABELS[r.key], true), size, font, true, color, asc(true)));
            var v = amountText(r);
            var vRight = rx + riw - pctW - size * 1.0;
            out.items.push(textItem(v, vRight - mw(v, true), yr, mw(v, true), size, font, true, color, asc(true)));
            if (r.akg != null) {
                var p = r.akg + '%';
                out.items.push(textItem(p, rx + riw - mw(p, true), yr, mw(p, true), size, font, true, color, asc(true)));
            }
            yr += lh;
        });
        var top = Math.max(yl, yr) + pad * 0.3;
        // vertical divider between the two halves
        out.items.push({ kind: 'shape', shape: 'rect', box: { x: x0 + leftW - thin / 2, y: y0, w: thin, h: top - y0 }, fill: color });
        hline(out, x0, top, w, thin, color);
        var yf = top + lh * 0.15;
        var foot = wrapRuns([{ t: FOOT, b: false, i: true }], w - 2 * pad, size, font, measure);
        yf += emitPara(out, foot, x0 + pad, yf, w - 2 * pad, size, font, color, 'left', asc);
        yf += pad * 0.5;
        strokeRect(out, x0, y0, w, yf - y0, thin * 1.4, color);
        return { h: yf - y0, w: w, x: x0 };
    }

    function ingLinear(res, opts, x0, y0, w, b, font, measure, out) {
        var color = opts.color || '#000000';
        var size = b;
        var pad = Math.max(0.7, size * 0.4);
        var thin = Math.max(0.15, size * 0.06);
        var runs = [{ t: 'INFORMASI NILAI GIZI ', b: true }];
        if (res.basis === 'serving') {
            var spp = sppLine(res);
            runs.push({ t: servingLine(res, opts.urt) + (spp ? ', ' + spp : '') + ' ', b: false });
        }
        runs.push({ t: headLine(res) + ' : ', b: true });
        res.rows.forEach(function (r, i) {
            runs.push({ t: LABELS[r.key] + ' ' + amountText(r), b: true });
            if (r.akg != null && res.basis !== '100') runs.push({ t: ' (' + r.akg + '% AKG)', b: true });
            runs.push({ t: i < res.rows.length - 1 ? ', ' : '. ', b: false });
        });
        if (res.basis !== '100') runs.push({ t: FOOT, b: false, i: true });
        var lines = wrapRuns(runs, w - 2 * pad, size, font, measure);
        var h = emitPara(out, lines, x0 + pad, y0 + pad * 0.6, w - 2 * pad, size, font, color, 'left',
            function (bd) { return measure.ascentEm({ font: font, bold: bd, size: size }); });
        var total = h + pad * 1.2;
        strokeRect(out, x0, y0, w, total, thin * 1.6, color);
        return { h: total, w: w, x: x0 };
    }

    /* ── the label ───────────────────────────────────────────────────────
     * spec: {
     *   w, h               label size, mm
     *   mode               'single' | 'cols' | 'front_back'
     *   pad                inner margin (safe zone), mm
     *   font, color        face id of PdfTextFont, text colour
     *   blocks: [ {id, panel:'main'|'info', type, …} ]   in reading order
     * }
     * block types:
     *   para    {runs:[{t,b,i}], role:'body'|'prominent'|'title'|'brand', align}
     *   mark    {d, vw, vh, fill, bg, frameOnly?, heightMm, align, capRatio}
     *           (vector path from non-halal-marks.json, drawn as ext)
     *   image   {src, ratio, heightMm, align, caption:{runs}}  (halal label)
     *   halal   {src, ratio, number, color, heightMm, numberOutside}
     *   ing     {res, format, urt}
     *   qr      {value, sizeMm, caption}
     *   ean     {value, widthMm, heightMm}
     *   row     {left: <halal|mark block>, right: [para blocks], gap}
     *   notice  {runs, color, bg, pig:{d,vw,vh}}  (framed warning, Pasal 41)
     */
    function layout(spec, measure) {
        var areaCm2 = spec.w * spec.h / 100;
        var R = window_rules();
        var oMin = {
            body: R.oMinFor('body', areaCm2),
            prominent: R.oMinFor('prominent', areaCm2),
        };
        var font = spec.font || 'noto';
        var oRatioReg = measure.oRatio({ font: font, bold: false, size: 10 });
        var oRatioBold = measure.oRatio({ font: font, bold: true, size: 10 });
        var oRatio = Math.min(oRatioReg, oRatioBold);
        var sizeForO = function (o) { return o / oRatio; };
        var bMin = sizeForO(oMin.body);
        var pad = spec.pad == null ? 2 : spec.pad;
        // 'single'     one column, everything on the label (= the main panel);
        // 'cols'       the same label in two columns: main fields left, the
        //              information panel (ingredients, ING…) right;
        // 'front_back' two labels: front (main panel) and back.
        var mode = spec.mode || 'single';
        var panels = mode === 'single' ? ['main'] : ['main', 'info'];
        var geo = {};
        if (mode === 'cols') {
            var gap = Math.max(1.5, spec.w * 0.025);
            var leftW = (spec.w - 2 * pad - gap) * (spec.split == null ? 0.5 : spec.split);
            geo.main = { x: pad, w: leftW };
            geo.info = { x: pad + leftW + gap, w: spec.w - 2 * pad - leftW - gap, divider: pad + leftW + gap / 2 };
        } else {
            geo.main = { x: pad, w: spec.w - 2 * pad };
            geo.info = { x: pad, w: spec.w - 2 * pad };
        }

        function attempt(b) {
            var roles = rolesFor(b, sizeForO, oMin);
            var res = { b: b, roles: roles, panels: {}, audit: [], fits: true, over: {}, mode: mode };
            panels.forEach(function (p) {
                var blocks = (spec.blocks || []).filter(function (bl) {
                    return panels.length === 1 || panelOf(bl) === p;
                });
                var r = flow(blocks, spec, roles, b, font, measure, sizeForO, oRatio, oMin, geo[p]);
                res.panels[p] = r;
                res.audit = res.audit.concat(r.audit);
                var free = spec.h - 2 * pad;
                if (r.h > free + 1e-6) { res.fits = false; res.over[p] = r.h - free; }
            });
            // two columns are ONE label: merge them for the renderers
            if (mode === 'cols') {
                var m = res.panels.main, inf = res.panels.info;
                res.label = { items: m.items.concat(inf.items), ext: m.ext.concat(inf.ext) };
                if (spec.divider !== false && inf.items.length) {
                    res.label.items.push({ kind: 'shape', shape: 'rect', fill: spec.dividerColor || '#bbbbbb',
                        box: { x: geo.info.divider - 0.1, y: pad, w: 0.2, h: spec.h - 2 * pad } });
                }
            }
            return res;
        }

        // largest b that fits, between the legal floor and B_MAX
        function solve() {
            var lo = bMin, hi = Math.max(bMin, Math.min(B_MAX, spec.h / 6));
            var best = attempt(lo);
            if (!best.fits) {
                best.floor = true;
                return best;
            }
            for (var it = 0; it < 18; it++) {
                var mid = (lo + hi) / 2;
                var r = attempt(mid);
                if (r.fits) { best = r; lo = mid; } else hi = mid;
            }
            var top = attempt(hi);
            if (top.fits) best = top;
            return best;
        }
        /* Front + back: the main panel MUST carry the Pasal 5(2) fields, the
           rest may go on either side. When the back overflows, the first
           information blocks (ingredients, allergen statement…) move to the
           free space of the front, in reading order — the fewest that make
           the label fit. */
        var moved = 0;
        var infoIds = (spec.blocks || []).filter(function (bl) { return (bl.panel || 'main') === 'info'; }).map(function (bl) { return bl.id; });
        function panelOf(bl) {
            var p = bl.panel || 'main';
            if (p === 'info' && mode === 'front_back' && infoIds.indexOf(bl.id) < moved) return 'main';
            return p;
        }
        var best;
        if (mode === 'front_back' && infoIds.length) {
            // the fewest moved blocks that fit; none fits → the least overflow
            for (var mk = 0; mk <= infoIds.length; mk++) {
                moved = mk;
                var r3 = solve();
                r3.moved = mk;
                if (!best || score(r3) > score(best) + 1e-6) best = r3;
                if (r3.fits) break;
            }
            moved = best.moved;
            best = solve();
            best.moved = moved;
        } else if (mode === 'cols' && spec.split == null) {
            /* Where the columns meet is not a constant: a short main panel and
               a long ING column want the line further left. The split that
               lets the type be largest wins (ties: nearer the middle). */
            var cands = [0.5, 0.44, 0.56, 0.38, 0.62, 0.34];
            for (var ci = 0; ci < cands.length; ci++) {
                setSplit(cands[ci]);
                var r2 = solve();
                r2.split = cands[ci];
                if (!best || score(r2) > score(best) + 1e-6) best = r2;
            }
            var chosen = best.split;
            setSplit(chosen);
            best = solve();
            best.split = chosen;
        } else {
            best = solve();
        }
        function score(r) { return (r.fits ? 1000 : 0) + (r.fits ? r.b : -Math.max.apply(null, Object.keys(r.over).map(function (k) { return r.over[k]; }))); }
        function setSplit(f) {
            var gap = Math.max(1.5, spec.w * 0.025);
            var leftW = (spec.w - 2 * pad - gap) * f;
            geo.main = { x: pad, w: leftW };
            geo.info = { x: pad + leftW + gap, w: spec.w - 2 * pad - leftW - gap, divider: pad + leftW + gap / 2 };
        }
        best.bMin = bMin;
        best.oRatio = oRatio;
        best.oMin = oMin;
        best.areaCm2 = areaCm2;
        return best;
    }

    var _rules = null;
    function window_rules() {
        if (_rules) return _rules;
        _rules = (typeof self !== 'undefined' && self.HalalRules) || (typeof window !== 'undefined' && window.HalalRules);
        if (!_rules) throw new Error('HalalRules not loaded');
        return _rules;
    }
    function useRules(r) { _rules = r; }

    /* Flow one panel top to bottom. Returns {items, ext, h, audit}. */
    function flow(blocks, spec, roles, b, font, measure, sizeForO, oRatio, oMin, g) {
        var pad = spec.pad == null ? 2 : spec.pad;
        var x = g ? g.x : pad, w = g ? g.w : spec.w - 2 * pad;
        var y = pad;
        var out = { items: [], ext: [], audit: [] };
        var color = spec.color || '#000000';
        var bodyLh = b * LINE;

        blocks.forEach(function (bl, idx) {
            if (idx > 0) y += bodyLh * PARA_GAP;
            var h = place(bl, x, y, w);
            y += h;
        });
        out.h = y - pad;
        return out;

        function place(bl, bx, by, bw) {
            if (bl.type === 'para') return para(bl, bx, by, bw);
            if (bl.type === 'mark') return mark(bl, bx, by, bw);
            if (bl.type === 'halal') return halal(bl, bx, by, bw);
            if (bl.type === 'ing') return ing(bl, bx, by, bw);
            if (bl.type === 'qr') return qr(bl, bx, by, bw);
            if (bl.type === 'ean') return ean(bl, bx, by, bw);
            if (bl.type === 'row') return row(bl, bx, by, bw);
            if (bl.type === 'notice') return notice(bl, bx, by, bw);
            return 0;
        }

        /* A statement in a coloured frame — PerBPOM 31/2018 Pasal 41: red text
           in a red box on white, with the pig (traced from BPJPH's mark), and
           letters >= 2 mm (Pasal 42). */
        function notice(bl, bx, by, bw) {
            var role = bl.role || 'prominent';
            var size = roles[role] || b;
            var fpad = Math.max(0.8, size * 0.35);
            var sw = Math.max(0.3, size * 0.12);
            var pigH = 0, pigW = 0;
            if (bl.pig) { pigH = size * LINE * 1.7; pigW = pigH * bl.pig.vw / bl.pig.vh; }
            var tw = bw - 2 * fpad - (pigW ? pigW + fpad : 0);
            var lines = wrapRuns(bl.runs, tw, size, font, measure);
            var th = lines.length * size * LINE;
            var inner = Math.max(th, pigH);
            if (bl.bg) out.items.push({ kind: 'shape', shape: 'rect', box: { x: bx, y: by, w: bw, h: inner + 2 * fpad }, fill: bl.bg });
            emitPara(out, lines, bx + fpad, by + fpad + (inner - th) / 2, tw, size, font, bl.color || color, 'left',
                function (bd) { return measure.ascentEm({ font: font, bold: bd, size: size }); });
            if (bl.pig) {
                out.ext.push({ kind: 'path', d: bl.pig.d, vw: bl.pig.vw, vh: bl.pig.vh, fill: bl.color || color,
                    box: { x: bx + bw - fpad - pigW, y: by + fpad + (inner - pigH) / 2, w: pigW, h: pigH }, id: bl.id + '-pig' });
            }
            out.items.push({ kind: 'shape', shape: 'rect', box: { x: bx, y: by, w: bw, h: inner + 2 * fpad },
                stroke: bl.color || color, strokeWidth: sw, fill: null });
            // letter height: capitals of the face ~ 0.71 em (Noto Sans); the
            // act asks 2 mm of "ukuran huruf" — audited on the lower-case "o"
            // like every other warning (the stricter reading).
            var o = size * oRatio;
            out.audit.push({ id: bl.id, kind: 'text', role: 'prominent', size: size, o: o, oMin: oMin.prominent, ok: o + 1e-6 >= oMin.prominent });
            return inner + 2 * fpad;
        }

        function para(bl, bx, by, bw) {
            var role = bl.role || 'body';
            var size = roles[role] || b;
            var lines = wrapRuns(bl.runs, bw, size, font, measure);
            var h = emitPara(out, lines, bx, by, bw, size, font, bl.color || color, bl.align || 'left',
                function (bd) { return measure.ascentEm({ font: font, bold: bd, size: size }); });
            var legalRole = (role === 'title' || role === 'prominent') ? 'prominent' : 'body';
            if (role === 'brand') legalRole = 'body';
            var o = size * oRatio;
            out.audit.push({ id: bl.id, kind: 'text', role: legalRole, size: size, o: o,
                oMin: oMin[legalRole], ok: o + 1e-6 >= oMin[legalRole] });
            return h;
        }

        function mark(bl, bx, by, bw) {
            var hmm = bl.heightMm;
            var wmm = hmm * bl.vw / bl.vh;
            if (wmm > bw) { wmm = bw; hmm = wmm * bl.vh / bl.vw; }
            var mx = bl.align === 'left' ? bx : bx + (bw - wmm) / 2;
            out.ext.push({ kind: 'path', d: bl.d, vw: bl.vw, vh: bl.vh, fill: bl.fill, bg: bl.bg,
                box: { x: mx, y: by, w: wmm, h: hmm }, id: bl.id });
            if (bl.capRatio) {
                var cap = hmm * bl.capRatio;
                // BPOM Pasal 42: letters of the pork mark >= 2 mm
                out.audit.push({ id: bl.id, kind: 'mark', cap: cap, capMin: bl.capMin || 0,
                    ok: !bl.capMin || cap + 1e-6 >= bl.capMin });
            }
            return hmm;
        }

        /* Label Halal = the logo file from SIHALAL + the certificate number +
           a border (BPJPH FAQ "Label Halal" 2, 5, 6). The number goes under
           the logo inside the border; outside only when it cannot fit
           (Kepkaban 145/2022, as quoted by BPJPH). */
        function halal(bl, bx, by, bw) {
            var hmm = bl.heightMm;
            var padIn = Math.max(0.6, hmm * 0.06);
            /* The number lives INSIDE the border (BPJPH FAQ 5): a long SH
               number («ID35110012345670324», 19 signs) at body size is wider
               than the logo column, and was drawn across the border and off
               the label (QA 23.10.2026). It takes the size that fits the
               column, never under the legal "o"; still wider at that floor —
               the box grows and the row puts the right column below. */
            var numFloor = sizeForO(oMin.body);
            var numSize = Math.max(b, numFloor);
            var numW = measure(bl.number, { font: font, bold: true, size: numSize });
            var room = bw - padIn * 2;
            if (numW > room && room > 0) {
                numSize = Math.max(numFloor, numSize * room / numW);
                numW = measure(bl.number, { font: font, bold: true, size: numSize });
            }
            var numLh = numSize * LINE;
            var logoH = hmm - numLh - padIn * 2;
            if (logoH < hmm * 0.4) logoH = hmm * 0.6;
            var logoW = logoH * (bl.ratio || 0.8);
            var boxW = Math.max(logoW, bl.numberOutside ? 0 : numW) + padIn * 2;
            var boxH = logoH + padIn * 2 + (bl.numberOutside ? 0 : numLh);
            var hx = bl.align === 'left' ? bx : bx + (bw - Math.max(boxW, numW)) / 2;
            out.items.push({ kind: 'image', src: bl.src, fit: 'contain',
                box: { x: hx + (boxW - logoW) / 2, y: by + padIn, w: logoW, h: logoH } });
            var ny = by + padIn + logoH + (bl.numberOutside ? padIn * 2 + 0.3 : padIn * 0.2);
            out.items.push(textItem(bl.number, hx + Math.max(0, (boxW - numW) / 2), ny, numW, numSize, font, true,
                bl.color, measure.ascentEm({ font: font, bold: true, size: numSize })));
            out.items.push({ kind: 'shape', shape: 'rect', box: { x: hx, y: by, w: boxW, h: boxH },
                stroke: bl.color, strokeWidth: Math.max(0.25, hmm * 0.02), fill: null, radius: 0 });
            var o = numSize * oRatio;
            out.audit.push({ id: bl.id, kind: 'text', role: 'body', size: numSize, o: o, oMin: oMin.body, ok: o + 1e-6 >= oMin.body });
            var total = boxH + (bl.numberOutside ? numLh + 0.3 : 0);
            out.halalBox = { x: hx, y: by, w: boxW, h: total };
            return total;
        }

        function ing(bl, bx, by, bw) {
            var r = ingBlock(bl.res, { format: bl.format, urt: bl.urt, color: color }, bx, by, bw, b, font, measure, out);
            var o = b * oRatio;
            out.audit.push({ id: bl.id, kind: 'text', role: 'body', size: b, o: o, oMin: oMin.body, ok: o + 1e-6 >= oMin.body });
            return r.h;
        }

        function qr(bl, bx, by, bw) {
            var s = Math.min(bl.sizeMm || 16, bw);
            var qx = bx + (bw - s) / 2;
            out.items.push({ kind: 'barcode', symbology: 'qrcode', value: bl.value, showText: false,
                box: { x: qx, y: by, w: s, h: s } });
            var h = s;
            if (bl.caption) {
                h += 0.4 + para({ id: bl.id + '-cap', runs: bl.caption, role: 'body', align: 'center' }, bx, by + s + 0.4, bw);
            }
            return h;
        }

        function ean(bl, bx, by, bw) {
            var wmm = Math.min(bl.widthMm || 30, bw), hmm = bl.heightMm || 18;
            out.items.push({ kind: 'barcode', symbology: 'ean13', value: bl.value, showText: true,
                box: { x: bx + (bw - wmm) / 2, y: by, w: wmm, h: hmm } });
            return hmm;
        }

        function row(bl, bx, by, bw) {
            var gap = bl.gap == null ? Math.max(1.2, b * 0.8) : bl.gap;
            // measure the left block first (marks and the halal label size
            // themselves), then flow the right column in what is left
            var saveItems = out.items.length, saveExt = out.ext.length, saveAudit = out.audit.length;
            var leftH = place(Object.assign({}, bl.left, { align: 'left' }), bx, by, bw * 0.45);
            var leftW = measureLeftWidth(saveItems, saveExt, bx);
            var rx = bx + leftW + gap, rw = bw - leftW - gap;
            /* Side by side only while every word of the right column fits its
               width: «P-IRT No. 2063507010123-29» beside a wide halal box was
               cut mid-word («P-IR / T», QA 23.10.2026). Otherwise the halal
               label takes its row and the right column goes under it. */
            if (rw < longestWord(bl.right || [])) {
                out.items.length = saveItems; out.ext.length = saveExt; out.audit.length = saveAudit;
                leftH = place(Object.assign({}, bl.left, { align: 'left' }), bx, by, bw);
                var sy = by + leftH;
                (bl.right || []).forEach(function (p) {
                    sy += bodyLh * 0.2;
                    sy += place(p, bx, sy, bw);
                });
                return sy - by;
            }
            var ry = by;
            (bl.right || []).forEach(function (p, i) {
                if (i) ry += bodyLh * 0.2;
                ry += place(p, rx, ry, rw);
            });
            return Math.max(leftH, ry - by);
        }

        function longestWord(blocks) {
            var w = 0;
            blocks.forEach(function (p) {
                if (p.type !== 'para') return;
                var size = roles[p.role || 'body'] || b;
                (p.runs || []).forEach(function (r) {
                    String(r.t || '').split(/\s+/).forEach(function (word) {
                        if (word) w = Math.max(w, measure(word, { font: font, bold: !!r.b, size: size }));
                    });
                });
            });
            return w;
        }

        function measureLeftWidth(i0, e0, bx) {
            var right = bx;
            for (var i = i0; i < out.items.length; i++) right = Math.max(right, out.items[i].box.x + out.items[i].box.w);
            for (var j = e0; j < out.ext.length; j++) right = Math.max(right, out.ext[j].box.x + out.ext[j].box.w);
            return right - bx;
        }
    }

    /* A repeating sheet of one label on office paper — A4 or F4 (215 × 330,
       the paper of an Indonesian office and print shop). `Sheets` is
       LabelSheets (label-maker/sheets.js). As many as fit with the printer's
       common border (DEAD_ZONE_MM, 5 mm) on the sides and bottom, 9 mm on top
       for the 50 mm scale bar (rulerSlot), gaps of 2–6 mm to cut between, the
       block centred. With the old fixed 8 mm margins and 3 mm gaps a 100 × 70
       label went ONE across an F4 sheet that takes two — 4 labels where 8 fit
       (QA 23.10.2026). */
    function gridFormat(Sheets, pageId, w, h) {
        var P = Sheets.page(pageId);
        var side = Sheets.DEAD_ZONE_MM || 5, top = 9, minGap = 2, maxGap = 6;
        var cols = Math.max(1, Math.floor((P.w - 2 * side + minGap) / (w + minGap)));
        var rows = Math.max(1, Math.floor((P.h - top - side + minGap) / (h + minGap)));
        var gx = cols > 1 ? Math.min(maxGap, (P.w - 2 * side - cols * w) / (cols - 1)) : 0;
        var gy = rows > 1 ? Math.min(maxGap, (P.h - top - side - rows * h) / (rows - 1)) : 0;
        var bw = cols * w + (cols - 1) * gx, bh = rows * h + (rows - 1) * gy;
        return Sheets.custom({ page: pageId, cols: cols, rows: rows, w: w, h: h,
            x0: (P.w - bw) / 2, y0: Math.max(top, (P.h - bh) / 2), dx: w + gx, dy: h + gy });
    }

    return {
        gridFormat: gridFormat,
        LINE: LINE,
        OBLIQUE_DEG: OBLIQUE_DEG,
        FOOT: FOOT,
        LABELS: LABELS,
        wrapRuns: wrapRuns,
        ingText: ingText,
        ingBlock: ingBlock,
        layout: layout,
        useRules: useRules,
        rolesFor: rolesFor,
    };
});
